import { CellPaint } from './shortfallSeries'

const SVG_NS = 'http://www.w3.org/2000/svg'

/** Класс наших наложений — по нему же они и снимаются перед перерисовкой. */
const OVERLAY_CLASS = 'sf-heat-cell'

/** Плавное проявление половин — под анимацию входа самого графика. */
const FADE_MS = 450

/**
 * Метка на ячейке: половины для неё уже нарисованы. Нужна, чтобы перерисовка
 * запускалась только когда Apex ЗАМЕНИЛ ячейки, а не на любое изменение DOM —
 * при наведении он трогает подсветку и тултип десятки раз в секунду, и слепая
 * перерисовка давала мигание половин.
 */
const PAINTED_ATTR = 'data-sf-painted'

/**
 * Половина ячейки со скруглением ТОЛЬКО по внешним углам: на стыке посередине
 * скруглений быть не должно, иначе две половины читаются как две отдельные
 * плитки, а не как один поделённый кубик.
 */
function halfPath(x: number, y: number, w: number, h: number, r: number, side: 'top' | 'bottom') {
  const radius = Math.max(0, Math.min(r, w / 2, h))

  return side === 'top'
    ? `M ${x} ${y + radius} Q ${x} ${y} ${x + radius} ${y} H ${x + w - radius}` +
        ` Q ${x + w} ${y} ${x + w} ${y + radius} V ${y + h} H ${x} Z`
    : `M ${x} ${y} H ${x + w} V ${y + h - radius}` +
        ` Q ${x + w} ${y + h} ${x + w - radius} ${y + h} H ${x + radius}` +
        ` Q ${x} ${y + h} ${x} ${y + h - radius} Z`
}

function label(x: number, y: number, text: string, ink: string) {
  const node = document.createElementNS(SVG_NS, 'text')
  node.setAttribute('x', String(x))
  node.setAttribute('y', String(y))
  node.setAttribute('text-anchor', 'middle')
  // Числа центрируются по своей половине именно так: ручной сдвиг «на глаз»
  // разъезжается, как только меняется высота ячейки.
  node.setAttribute('dominant-baseline', 'central')
  node.setAttribute('font-size', '11')
  node.setAttribute('font-weight', '600')
  node.setAttribute('fill', ink)
  node.setAttribute('fill-opacity', '1')
  node.textContent = text
  return node
}

/**
 * Ячейка heatmap в Apex — один `rect` с одним цветом: `fillColor` у точки он не
 * читает, а цвет диапазона гонит через `rgb2hex` в анимации (проверено по
 * исходникам 3.54 и 6.8 — `charts/HeatMap.js`). Поэтому **обе** половины ячейки
 * — верхнюю (депозиты) и нижнюю (выплаты) — рисуем сами поверх готовой ячейки,
 * а Apex красит саму ячейку нейтралью темы.
 *
 * Рисовать надо именно обе: если верх оставить за Apex, а снизу класть свою
 * половину, они разъезжаются по ширине и скруглению — у ячейки Apex своя обводка
 * и `rx` по всем четырём углам. Ровно этот дефект такой подход и заменил.
 *
 * За Apex остаётся геометрия сетки, оси, ховер и тултип. Наложения мышь не
 * перехватывают (`pointer-events: none`), иначе ховер и тултип ломались бы;
 * перед каждой перерисовкой старые снимаются, так что узлы не копятся.
 *
 * Тот же приём, что у `dayDividerEvents`: размеры ячеек известны только после
 * отрисовки, отсюда события `mounted`/`updated`, а не опции.
 */
export function paintHeatCells(
  root: Element | null,
  paintFor: (seriesIndex: number, hour: number) => CellPaint,
  /** Перерисовать, даже если ячейки те же, — данные или тема изменились. */
  force = false
) {
  {
    if (!root || typeof root.querySelectorAll !== 'function') return

    const cells = root.querySelectorAll('rect.apexcharts-heatmap-rect')
    if (!cells.length) return

    // Все ячейки — те же, что мы уже обслужили: рисовать нечего.
    const fresh = root.querySelectorAll(`rect.apexcharts-heatmap-rect:not([${PAINTED_ATTR}])`)
    if (!force && !fresh.length && root.querySelectorAll(`.${OVERLAY_CLASS}`).length) return

    root.querySelectorAll(`.${OVERLAY_CLASS}`).forEach((node) => node.remove())

    cells.forEach((node) => {
      node.setAttribute(PAINTED_ATTR, '1')
      const rect = node as SVGRectElement
      const seriesIndex = Number(rect.getAttribute('i'))
      const hour = Number(rect.getAttribute('j'))
      if (!Number.isFinite(seriesIndex) || !Number.isFinite(hour)) return

      const paint = paintFor(seriesIndex, hour)
      if (!paint.top && !paint.bottom) return

      const x = Number(rect.getAttribute('x'))
      const y = Number(rect.getAttribute('y'))
      const width = Number(rect.getAttribute('width'))
      const height = Number(rect.getAttribute('height'))
      if (![x, y, width, height].every(Number.isFinite) || width <= 0 || height <= 0) return

      const radius = Number(rect.getAttribute('rx')) || 0
      // Промежуток между ячейками рисует обводка самой ячейки: если закрасить её
      // площадь целиком, соседние кубики сливаются. Поэтому половины поджимаем
      // внутрь на толщину обводки.
      const inset = Number(rect.getAttribute('stroke-width')) || 0
      const cellX = x + inset
      const cellW = Math.max(0, width - inset * 2)
      const cellY = y + inset
      const cellH = Math.max(0, height - inset * 2)
      const half = cellH / 2

      const group = document.createElementNS(SVG_NS, 'g')
      group.classList.add(OVERLAY_CLASS)
      group.setAttribute('pointer-events', 'none')
      group.style.opacity = '0'
      group.style.transition = `opacity ${FADE_MS}ms ease-out`
      ;(['top', 'bottom'] as const).forEach((side) => {
        const part = side === 'top' ? paint.top : paint.bottom
        if (!part) return

        const top = side === 'top' ? cellY : cellY + half
        const shape = document.createElementNS(SVG_NS, 'path')
        shape.setAttribute('d', halfPath(cellX, top, cellW, half, radius, side))
        shape.setAttribute('fill', part.color)
        // Группа серии Apex несёт свою fill-opacity (по умолчанию 0.85), и наши
        // половины её наследуют — цвета выцветают, а подписи сереют.
        shape.setAttribute('fill-opacity', '1')
        group.appendChild(shape)

        if (part.label)
          group.appendChild(label(cellX + cellW / 2, top + half / 2, part.label, part.ink))
      })

      // В ту же группу серии — она несёт трансформацию, и половины попадают
      // ровно на свою ячейку.
      rect.parentNode?.appendChild(group)
      requestAnimationFrame(() => {
        group.style.opacity = '1'
      })
    })
  }

}

/**
 * События графика — один из двух путей перерисовки. Второй, обязательный, живёт
 * в самом компоненте (`useEffect` после рендера): Apex шлёт `updated` не на
 * каждом пути обновления, и при переключении вида половины иначе не
 * возвращаются — ячейки остаются серыми.
 */
export function heatSplitEvents(paintFor: (seriesIndex: number, hour: number) => CellPaint) {
  const draw = (ctx: any) => paintHeatCells(ctx?.el ?? null, paintFor)

  return { mounted: draw, updated: draw, animationEnd: draw }
}

/** Склейка обработчиков событий графика: у Apex на событие один колбэк. */
export function mergeChartEvents(...handlers: Array<Record<string, (ctx: any) => void>>) {
  const merged: Record<string, (ctx: any) => void> = {}

  handlers.forEach((handler) => {
    Object.entries(handler).forEach(([event, fn]) => {
      const previous = merged[event]
      merged[event] = previous
        ? (ctx: any) => {
            previous(ctx)
            fn(ctx)
          }
        : fn
    })
  })

  return merged
}
